import { Block, Accordion, PrensaEngine, Typography } from '@xprog/prensa'
import { AccordionItemGroupProps } from '@xprog/prensa/types'
import { SideMenuServiceButton } from 'components/Buttons'
import { AssetsContext } from "providers/Assets";
import Icon from 'components/Icon/Icon.index'
import SearchInputBox from 'components/SearchInput/SearchInputBox.index'
import { useIcon } from 'hooks/useIcon'
import Link from 'next/link'
import React, { useContext } from "react";

import { SideMenuSectionTitle } from '../SideMenuSectionTitle/SideMenuSectionTitle'
import { SideMenuSectionContentProps } from './props'
import { ServerSidePropsContext } from 'providers/ServerSideProps'
import { get } from 'lodash'
import ClickableAsset from 'components/ClickableAsset/ClickableAsset.index'
import { SubscriberInfo } from '../SubscriberInfo/SubscriberInfo.index';
import { SessionDataContext } from 'providers/Session';

const HandleLink = (props) => {
  return <Link legacyBehavior {...props} />
}

const SubItemGroup = PrensaEngine.styled('ul', {
  p: '$0',
  m: '$0',
  'list-style-type': 'none'
})

const SubItem = PrensaEngine.styled('li', {
  align: ['row', 'left', 'middle'],
  height: 'fit-content',
  py: '$2'
})

const SubItems = ({ data }) => {
  return (
    <SubItemGroup>
      {data.map((i, k) => (
        <SubItem key={`${i.id}-${k}`}>
          <Typography
            as='span'
            css={{
              fontFamily: '$primary',
              fontSize: '$caption5',
              fontWeight: '$caption5',
              color: '$basicBlack',
              my: '$0'
            }}
          >
            <a href={i.path}>{i.name}</a>
          </Typography>
        </SubItem>
      ))}
    </SubItemGroup>
  )
}

export const SideMenuSectionContent: React.FC<SideMenuSectionContentProps> = ({ config, navItems, onClose, onLogin, onLogout, variant}) => {
  const { enabled, searchInput, serviceButtons, navigationAccordions, advertiseSection } = config
  const site = process.env.NEXT_PUBLIC_WEBAPP_CLIENT_SLUG;
  const collapseIcon = useIcon('IcSetaFechar')
  const expandIcon = useIcon('IcSetaAbrir')

  const assetsContext = useContext(AssetsContext);
  const sessionData = useContext(SessionDataContext);

  const banners = assetsContext?.assets?.banners;
  const bannerAnuncie = banners.find((i) => i.name === 'BannerAssineAgora')

  const serverSideContext = React.useContext(ServerSidePropsContext);
  const shouldRenderAcervo = get(serverSideContext, 'siteSlug', '')

  if (!enabled) {
    return null
  }

  const isPaywall =
    serverSideContext?.site?.pageConfig?.featureRadioSelectPaywall;
  const navigationAccordionItemCSS = navigationAccordions.css || {}


  const navigationAccordionItems: Array<AccordionItemGroupProps> = navItems
    ? navItems.map((i, k): AccordionItemGroupProps => {
      // Verifica se o título é "Publicidades" para aplicar um estilo personalizado
      const idPubSection = i.name === 'Publicidade';
      const pubTitleStyles = {
        title: {
          color: site == 'daqui' ? '$secondaryMain' : '$primaryMain',
          fontFamily: '$primary',
          fontSize: '$retrancaHamburger',
          fontWeight: '$retrancaHamburger',
          lineHeight: '$retrancaHamburger',
          m: '0px',
          pt: '$3',
          pl: '$8',
          pr: '$18',
          height: 'max-content',
          minHeight: '40px',
          cursor: 'default'
        },
      }

      if (idPubSection) {
        // Retorna apenas um texto, sem link ou drop-down
        return {
          id: `${i.path}-${k}`,
          title: i.name,
          children: null, // Não haverá filhos
          css: {
            accordionTitle: {
              ...pubTitleStyles,
              iconButton: {
                display: 'none'
              }
            },

          }
        };
      }

      return {
        id: `${i.path}-${k}`,
        title: i.name,
        href: i.path,
        iconExpandedState: <Icon {...collapseIcon} />,
        iconNotExpandedState: <Icon {...expandIcon} />,
        customLink: HandleLink,
        children: i.subItems ? <SubItems data={i.subItems} /> : undefined,
        css: {
          ...navigationAccordionItemCSS?.accordionItemGroup,
          accordionTitle: {

            ...navigationAccordionItemCSS?.accordionItemGroup?.accordionTitle,
            iconButton: {
              ...navigationAccordionItemCSS?.accordionItemGroup
                ?.accordionTitle?.iconButton,
              display: i.subItems ? 'unset' : 'none'
            }
          }
        }
      };
    })
    : [];

  if (shouldRenderAcervo === "tocantins") {
    navigationAccordionItems.push({
      id: 'acervo',
      title: 'Acervo',
      href: 'https://www.jornaldotocantins.com.br/digital/',
      iconExpandedState: <Icon {...collapseIcon} />,
      iconNotExpandedState: <Icon {...expandIcon} />,
      customLink: HandleLink,
      children: undefined,
      css: {
        ...navigationAccordionItemCSS?.accordionItemGroup,
        accordionTitle: {
          ...navigationAccordionItemCSS?.accordionItemGroup?.accordionTitle,
          iconButton: {
            ...navigationAccordionItemCSS?.accordionItemGroup
              ?.accordionTitle?.iconButton,
            display: 'none'
          }
        }
      }
    })
  }

  return (
    <Block css={{ width: '100%' }}>
      {searchInput.enabled && (
        <SearchInputBox
          buttonId='busca-menu'
          hasItemsIndicator={false}
          css={{
            container: { mt: '$6', mb: '$4' },
            input: { ml: '$3', width: 'calc(100% - $6)' },
            icon: { button: { mr: '$0' } }
          }}
        />
      )}

      {serviceButtons.enabled && (
        <Block
          css={{
            align: ['row', 'center', 'middle'],
            mb: '$2'
          }}
        >
          {serviceButtons.items.map((i) => (
            <SideMenuServiceButton
              key={i.id}
              href={i.href}
              variant={site === 'tocantins' && i.variant === 'JornalDigital' ? site : i.variant}
              target={i.target}
            />
          ))}
        </Block>
      )}

      {advertiseSection.enabled && !sessionData?.emailUsuario &&
        <Block
          css={{
            align: ['row', 'center', 'middle'],
            backgroundColor: advertiseSection.color,
            height: '100px',
            width: '100%'
          }}
        >
          <ClickableAsset
            id='assine-agora'
            alt='Assine'
            href={advertiseSection.href}
            src={bannerAnuncie.url}
            height={bannerAnuncie.height}
            width={bannerAnuncie.width}
            target='blank'
          />
        </Block>
      }
      {isPaywall !== "DESATIVADO" && (
        <SubscriberInfo
          amountLeft={
            sessionData?.inscricaoDegustador?.creditosDegustadorList[0]
              ?.quantidade
          }
          type={sessionData?.id ? "ASSINANTE" : variant}
          onLogin={onLogin}
          onLogout={onLogout}
          username={sessionData?.emailUsuario?.split("@")[0] || ""}
        />
      )}
      <SideMenuSectionTitle color={navigationAccordions.sectionTitleColor}>
        Editorias
      </SideMenuSectionTitle>
      <Accordion
        accordionTitleAs='p'
        items={navigationAccordionItems}
        size='fullWidth'
        css={{ accordionGroup: { border: 'unset' } }}
      />
      {navigationAccordions.items.map((i) => (
        <React.Fragment key={i.id}>
          <SideMenuSectionTitle color={navigationAccordions.sectionTitleColor}>
            {i.title}
          </SideMenuSectionTitle>
          <Accordion
            items={i.items}
            iconExpandedState={<Icon {...collapseIcon} />}
            iconNotExpandedState={<Icon {...expandIcon} />}
            size='fullWidth'
            css={{
              ...i.css,
              accordionGroup: { border: 'unset' },
              accordionItemGroup: {
                ...navigationAccordionItemCSS?.accordionItemGroup,
                accordionTitle: {
                  ...navigationAccordionItemCSS?.accordionItemGroup
                    ?.accordionTitle,
                  iconButton: {
                    ...navigationAccordionItemCSS?.accordionItemGroup
                      ?.accordionTitle?.iconButton,
                    display: 'none'
                  }
                }
              }
            }}
          />
        </React.Fragment>
      ))}
    </Block>
  )
}
